import is from '@sindresorhus/is';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent, KeyboardEvent } from 'react';
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import { useTranslate } from '~/shared/context/use-translate';
import type { Translate, TranslationKey } from '~/shared/internationalization/translation';
import { Morph } from '~/ui/animations/morph/Morph';
import { Button } from '~/ui/core/button/Button';
import { Input } from '~/ui/core/input/Input';
import { Spinner } from '~/ui/core/spinner/Spinner';
import { Textarea } from '~/ui/core/textarea/Textarea';

import { useSnackbars } from '../../app/error-handling/SnackBars';
import { jobArchitectureKeys } from '../api/query-keys';
import { useCompanyInformationGeneration } from '../api/use-company-information-generation';
import { useJobArchitectureMetadata } from '../api/use-job-architecture-metadata';
import { useSubmitCompanyInformationGeneration } from '../api/use-submit-company-information-generation';
import { useUpdateJobArchitectureMetadata } from '../api/use-update-job-architecture-metadata';
import { CompanyInformationLoadingSkeletons } from './CompanyInformationLoadingSkeletons';
import {
    Container,
    Description,
    ErrorMessage,
    Footer,
    FooterActions,
    FormContainer,
    FormField,
    InfoBanner,
    InfoBannerContent,
    InfoBannerDescription,
    InfoBannerTitle,
    Label,
    RequiredMark,
    Title,
    TitleGroup,
} from './setup-dialog-styles';
import {
    areMetadataFieldsComplete,
    COMPANY_SIZE_MAX,
    isCompanySizeInRange,
    useCompanyInformationForm,
} from './use-company-information-form';

const COMPANY_NAME_MAX_LENGTH = 120;
const COMPANY_DOMAIN_MAX_LENGTH = 255;

const getCompanySizeErrorKey = (value: string): TranslationKey | null => {
    if (is.emptyStringOrWhitespace(value)) {
        return 'pages.job-architecture-v2.setup-dialog.company-information.company-size.required';
    }
    const size = Number.parseInt(value, 10);
    if (Number.isNaN(size) || size < 1) {
        return 'pages.job-architecture-v2.setup-dialog.company-information.company-size.min-error';
    }
    if (size > COMPANY_SIZE_MAX) {
        return 'pages.job-architecture-v2.setup-dialog.company-information.company-size.max-error';
    }
    return null;
};

const getCompanySizeErrorMessage = (value: string, translate: Translate): string | null => {
    const key = getCompanySizeErrorKey(value);
    return is.null(key) ? null : translate(key);
};

type CompanyInformationContentProps = {
    readonly jobArchitectureId: string;
    readonly onBack?: () => void;
    readonly onContinue: () => void;
};

export const CompanyInformationContent: FunctionComponent<CompanyInformationContentProps> = ({
    jobArchitectureId,
    onBack,
    onContinue,
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: wizard page consolidates 5 UX states with local form state
}) => {
    const translate = useTranslate();
    const { data: metadata } = useJobArchitectureMetadata(jobArchitectureId);
    const { showErrorSnackbar } = useSnackbars();
    const queryClient = useQueryClient();

    const { isPending: isSubmittingGeneration, mutateAsync: submitGeneration } =
        useSubmitCompanyInformationGeneration();
    const { isPending: isSaving, mutateAsync: updateMetadata } = useUpdateJobArchitectureMetadata();

    const {
        confirmName,
        consumeRegenerateIntent,
        getValues,
        hadGenerationError,
        hydrateDomainIfEmpty,
        isAwaitingGeneration,
        isHydratedFromCompleteMetadata,
        isNameConfirmed,
        markForRegenerate,
        observeError,
        populateResult,
        register,
        rewrite,
        submittedCompanyDomain,
        submittedCompanyName,
        watch,
    } = useCompanyInformationForm(jobArchitectureId, metadata);
    const values = watch();

    const {
        errorCode: generationErrorCode,
        generatedForCompanyDomain,
        generatedForCompanyName,
        hasQueryError,
        isGenerating,
        result: generationResult,
    } = useCompanyInformationGeneration(jobArchitectureId, {
        enabled: isNameConfirmed && !isHydratedFromCompleteMetadata,
    });

    const isResultForCurrentAttempt =
        !is.null(submittedCompanyName) &&
        generatedForCompanyName === submittedCompanyName &&
        (generatedForCompanyDomain ?? '') === (submittedCompanyDomain ?? '');

    useEffect(() => {
        if (isResultForCurrentAttempt) {
            hydrateDomainIfEmpty(generatedForCompanyDomain ?? '');
        }
    }, [generatedForCompanyDomain, hydrateDomainIfEmpty, isResultForCurrentAttempt]);

    useEffect(() => {
        if (!is.null(generationResult) && isResultForCurrentAttempt) {
            populateResult(generationResult);
        }
    }, [generationResult, isResultForCurrentAttempt, populateResult]);

    useEffect(() => {
        if (!isAwaitingGeneration) return;
        if (hasQueryError || !is.null(generationErrorCode)) {
            observeError();
        }
    }, [generationErrorCode, hasQueryError, isAwaitingGeneration, observeError]);

    const isAiLoading = isAwaitingGeneration || isGenerating || isSubmittingGeneration;
    const showConfirmButton = !isNameConfirmed && values.companyName.trim().length >= 2;
    const showRewriteButton = isNameConfirmed && !isAiLoading;
    const showFields = isNameConfirmed && !isAiLoading;
    const showErrorBanner = hadGenerationError && !isAiLoading;
    const showDomainField = !isNameConfirmed || isAiLoading;

    const companySizeErrorMessage =
        showFields && !is.emptyStringOrWhitespace(values.companySize) && !isCompanySizeInRange(values.companySize)
            ? getCompanySizeErrorMessage(values.companySize, translate)
            : null;
    const hasCompanySizeError = !is.null(companySizeErrorMessage);

    const triggerAiLookup = useCallback(async () => {
        const { companyDomain, companyName } = getValues();
        if (is.undefined(jobArchitectureId) || companyName.trim().length < 2 || isAiLoading) {
            return;
        }

        const regenerate = consumeRegenerateIntent();
        const trimmedName = companyName.trim();
        const trimmedDomain = companyDomain.trim();
        confirmName(trimmedName, trimmedDomain);

        await submitGeneration({
            companyDomain: trimmedDomain,
            companyName: trimmedName,
            jobArchitectureId,
            regenerate,
        }).catch(() => observeError());
    }, [
        confirmName,
        consumeRegenerateIntent,
        getValues,
        isAiLoading,
        jobArchitectureId,
        observeError,
        submitGeneration,
    ]);

    const handleRewriteDescription = useCallback(() => {
        markForRegenerate();
        rewrite();

        if (!is.undefined(jobArchitectureId)) {
            queryClient.removeQueries({
                queryKey: jobArchitectureKeys.companyInformationGeneration(jobArchitectureId),
            });
        }
    }, [jobArchitectureId, markForRegenerate, queryClient, rewrite]);

    const handleTryAgain = useCallback(() => {
        markForRegenerate();
        void triggerAiLookup();
    }, [markForRegenerate, triggerAiLookup]);

    const handleNameKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter' && !isNameConfirmed) {
                event.preventDefault();
                void triggerAiLookup();
            }
        },
        [isNameConfirmed, triggerAiLookup],
    );

    const handleNextStep = async (): Promise<void> => {
        if (is.undefined(jobArchitectureId)) return;

        const submittedValues = getValues();
        if (!is.null(getCompanySizeErrorKey(submittedValues.companySize))) return;

        try {
            await updateMetadata({
                allowJsaRename: !isHydratedFromCompleteMetadata,
                jobArchitectureId,
                metadata: {
                    companyDescription: submittedValues.companyDescription,
                    companyName: submittedValues.companyName,
                    companyPriorities: submittedValues.companyPriorities,
                    companySize: Number.parseInt(submittedValues.companySize, 10),
                },
            });

            onContinue();
        } catch (error) {
            showErrorSnackbar(error);
        }
    };

    const isNextButtonDisabled = isSaving || isAiLoading || !areMetadataFieldsComplete(values);

    const nameRegistration = register('companyName', { maxLength: COMPANY_NAME_MAX_LENGTH });
    const domainRegistration = register('companyDomain', { maxLength: COMPANY_DOMAIN_MAX_LENGTH });
    const descriptionRegistration = register('companyDescription');
    const prioritiesRegistration = register('companyPriorities');
    const companySizeRegistration = register('companySize');

    return (
        <Container>
            <div>
                <TitleGroup>
                    <Title>{translate('pages.job-architecture-v2.setup-dialog.company-information.title')}</Title>
                    <Description>
                        {translate('pages.job-architecture-v2.setup-dialog.company-information.description')}
                    </Description>
                </TitleGroup>
                <FormContainer>
                    <FormField>
                        <Label htmlFor="company-name">
                            {translate('pages.job-architecture-v2.setup-dialog.company-information.company-name.label')}
                            <RequiredMark>*</RequiredMark>
                        </Label>
                        <div className="flex gap-m items-center">
                            <div className="flex-1">
                                <Input
                                    {...nameRegistration}
                                    className="text-sm! leading-5!"
                                    id="company-name"
                                    maxLength={COMPANY_NAME_MAX_LENGTH}
                                    placeholder={translate(
                                        'pages.job-architecture-v2.setup-dialog.company-information.company-name.placeholder',
                                    )}
                                    onKeyDown={handleNameKeyDown}
                                />
                            </div>
                            <div className="flex justify-end items-center min-w-[11rem] min-h-[2.25rem]">
                                {showConfirmButton && (
                                    <Button onClick={triggerAiLookup}>
                                        {translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.confirm',
                                        )}
                                    </Button>
                                )}
                                {showRewriteButton && (
                                    <Button variant="tertiary" onClick={handleRewriteDescription}>
                                        {translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.rewrite-description',
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </FormField>
                    <AnimatePresence initial={false}>
                        {showDomainField && (
                            <motion.div
                                key="company-domain"
                                animate={{ height: 'auto', marginTop: 0, opacity: 1 }}
                                exit={{ height: 0, marginTop: '-1.5rem', opacity: 0 }}
                                initial={{ height: 0, marginTop: '-1.5rem', opacity: 0 }}
                                style={{ overflow: 'hidden' }}
                                transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
                            >
                                <FormField>
                                    <Label htmlFor="company-domain">
                                        {translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.company-domain.label',
                                        )}
                                    </Label>
                                    <Input
                                        {...domainRegistration}
                                        className="text-sm! leading-5!"
                                        id="company-domain"
                                        maxLength={COMPANY_DOMAIN_MAX_LENGTH}
                                        placeholder={translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.company-domain.placeholder',
                                        )}
                                        onKeyDown={handleNameKeyDown}
                                    />
                                </FormField>
                            </motion.div>
                        )}
                    </AnimatePresence>
                    <div aria-busy={isAiLoading} aria-live="polite" role="status">
                        <span className="sr-only">
                            {isAiLoading
                                ? translate(
                                      'pages.job-architecture-v2.setup-dialog.company-information.sr.generating-announcement',
                                  )
                                : null}
                            {showFields && !hadGenerationError
                                ? translate(
                                      'pages.job-architecture-v2.setup-dialog.company-information.sr.loaded-announcement',
                                  )
                                : null}
                        </span>
                        {isAiLoading && <CompanyInformationLoadingSkeletons />}
                        {showErrorBanner && (
                            <InfoBanner>
                                <InfoBannerContent>
                                    <InfoBannerTitle>
                                        {translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.error.title',
                                        )}
                                    </InfoBannerTitle>
                                    <InfoBannerDescription>
                                        {translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.error.body',
                                        )}{' '}
                                        <button
                                            className="bg-transparent border-none cursor-pointer font-medium p-0 text-sm text-warning-900 underline"
                                            type="button"
                                            onClick={handleTryAgain}
                                        >
                                            {translate(
                                                'pages.job-architecture-v2.setup-dialog.company-information.error.try-again',
                                            )}
                                        </button>
                                    </InfoBannerDescription>
                                </InfoBannerContent>
                            </InfoBanner>
                        )}
                        {showFields && (
                            <div className="flex flex-col gap-4xl">
                                <FormField>
                                    <Label htmlFor="company-description">
                                        {translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.company-description.label',
                                        )}
                                        <RequiredMark>*</RequiredMark>
                                    </Label>
                                    <Textarea
                                        {...descriptionRegistration}
                                        autoResize={true}
                                        className="text-sm! leading-5!"
                                        id="company-description"
                                        placeholder={translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.company-description.placeholder',
                                        )}
                                        rows={8}
                                    />
                                </FormField>
                                <FormField>
                                    <Label htmlFor="top-priorities">
                                        {translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.top-priorities.label',
                                        )}
                                        <RequiredMark>*</RequiredMark>
                                    </Label>
                                    <Textarea
                                        {...prioritiesRegistration}
                                        autoResize={true}
                                        className="text-sm! leading-5!"
                                        id="top-priorities"
                                        placeholder={translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.top-priorities.placeholder',
                                        )}
                                        rows={8}
                                    />
                                </FormField>
                                <FormField>
                                    <Label htmlFor="company-size">
                                        {translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.company-size.label',
                                        )}
                                        <RequiredMark>*</RequiredMark>
                                    </Label>
                                    <Input
                                        {...companySizeRegistration}
                                        className="text-sm! leading-5!"
                                        id="company-size"
                                        max={COMPANY_SIZE_MAX}
                                        min={1}
                                        placeholder={translate(
                                            'pages.job-architecture-v2.setup-dialog.company-information.company-size.placeholder',
                                        )}
                                        type="number"
                                        variant={hasCompanySizeError ? 'error' : 'default'}
                                    />
                                    {hasCompanySizeError && <ErrorMessage>{companySizeErrorMessage}</ErrorMessage>}
                                </FormField>
                            </div>
                        )}
                    </div>
                </FormContainer>
            </div>
            <Footer>
                <FooterActions style={is.undefined(onBack) ? { justifyContent: 'flex-end' } : undefined}>
                    {!is.undefined(onBack) && (
                        <Button variant="tertiary" onClick={onBack}>
                            {translate('common.back')}
                        </Button>
                    )}
                    <Button disabled={isNextButtonDisabled} onClick={handleNextStep}>
                        {translate('common.continue')}
                        <Morph direction="vertical" stateKey={isSaving ? 'loading' : 'idle'}>
                            {isSaving ? <Spinner /> : null}
                        </Morph>
                    </Button>
                </FooterActions>
            </Footer>
        </Container>
    );
};

export const CompanyInformationPage: FunctionComponent = () => {
    const navigate = useNavigate();
    const { id: jobArchitectureId } = useParams<{ id: string }>();

    if (is.undefined(jobArchitectureId)) {
        return null;
    }

    return (
        <CompanyInformationContent
            jobArchitectureId={jobArchitectureId}
            onBack={() => navigate('../overview')}
            onContinue={() => navigate('../upload-data')}
        />
    );
};
